import {
  Customer,
  DEFAULT_QUOTE_SECTIONS,
  FrameworkContract,
  FrameworkContractItem,
  Quote,
  QuoteItem,
  ServiceItem,
} from '../types';
import { activeQuoteItems, computePosNumbers, quoteTotalsMode, round2 } from './quoteLogic';

/**
 * Rahmenvertrag aus einem Angebot ohne Endsumme.
 *
 * Ein Angebot mit totalsMode 'unitPrices' verkauft Einheitspreise, keinen
 * Leistungsumfang: es gibt kein Bauvorhaben, also auch kein Projekt. Wird es
 * angenommen, landen die Preise als Rahmenvertrag beim Kunden
 * (Customer.frameworkContracts, jsonb — keine Migration). Von dort holen
 * spaeter der Projekt-Assistent (LV) und neue Angebote die Positionen.
 *
 * Alles hier ist rein; geschrieben wird in crmService.acceptQuoteAsFrameworkContract.
 */

/** Angebot ohne Endsumme (nur Einheitspreise)? */
export const isUnitPriceQuote = (q: Pick<Quote, 'details'>): boolean =>
  quoteTotalsMode(q) === 'unitPrices';

/** Wurde das Angebot als Rahmenvertrag angenommen (kein Projekt)? */
export const isFrameworkAcceptedQuote = (q: Pick<Quote, 'details'>): boolean =>
  !!q.details?.frameworkContractId;

/** Anzeige-/Auswahlname — dieselbe Form, die details.frameworkRef im Angebot speichert. */
export const frameworkContractLabel = (c: Pick<FrameworkContract, 'title' | 'number'>): string =>
  `${c.title ? c.title + ' – ' : ''}${c.number}`;

/**
 * Vereinbarter Netto-Einheitspreis einer Position: Listenpreis abzueglich
 * Positionsrabatt und Gesamtrabatt des Angebots, EINMAL auf Cent gerundet.
 * Das ist der Preis, der ins Projekt-LV und in neue Angebote geht.
 */
export const frameworkItemNetUnitPrice = (
  item: Pick<FrameworkContractItem, 'unitPrice' | 'discountPercent'>,
  contractDiscountPercent = 0,
): number =>
  round2(
    item.unitPrice
      * (1 - (item.discountPercent ?? 0) / 100)
      * (1 - (contractDiscountPercent || 0) / 100),
  );

/**
 * Preispositionen aus dem Angebot: nur aktive (gestrichene entfallen), mit
 * Positionsnummer und Sektionsueberschrift. NEP bleibt mit Kennzeichen drin —
 * der Preis ist vereinbart, die Ausfuehrung nur auf Anforderung.
 */
export const frameworkItemsFromQuote = (quote: Pick<Quote, 'items' | 'details'>): FrameworkContractItem[] => {
  const sections = quote.details?.sections ?? DEFAULT_QUOTE_SECTIONS;
  const sectionTitle = new Map(sections.map((s) => [s.id, s.title]));
  return activeQuoteItems(computePosNumbers(sections, quote.items || [])).map((it) => {
    const title = it.sectionId ? sectionTitle.get(it.sectionId) : undefined;
    return {
      id: it.id,
      ...(it.catalogItemId ? { catalogItemId: it.catalogItemId } : {}),
      ...(it.posNumber ? { posNumber: it.posNumber } : {}),
      lvPosition: it.lvPosition || it.posNumber || '',
      title: it.title,
      unit: it.unit,
      unitPrice: Number(it.unitPrice) || 0,
      ...(it.discountPercent ? { discountPercent: it.discountPercent } : {}),
      ...(it.nep ? { nep: true } : {}),
      ...(title ? { sectionTitle: title } : {}),
      ...(it.category ? { category: it.category } : {}),
      ...(it.assignment ? { assignment: it.assignment } : {}),
      ...(it.bemerkung ? { bemerkung: it.bemerkung } : {}),
    };
  });
};

/** Eingaben des Annahmedialogs im Modus „Rahmenvertrag". */
export interface AcceptFrameworkValues {
  /** Vertragsnummer; leer = Bestellnummer des Kunden, sonst Auftragsnummer. */
  contractNumber: string;
  title: string;
  /** yyyy-mm-dd; leer = ohne Beginn. */
  validFrom: string;
  /** yyyy-mm-dd; leer = unbefristet. */
  validUntil: string;
  customerRef?: string;
  /** Beauftragt am (yyyy-mm-dd). */
  orderDate?: string;
  note?: string;
  /** Als Standard-Rahmenvertrag setzen (neue Angebote belegen ihn vor). */
  makeDefault: boolean;
  /** Wer annimmt (wird am Vertrag vermerkt). */
  actor?: { id?: string; name?: string };
}

/** Vorbelegung des Dialogs aus dem Angebot. */
export const frameworkAcceptDefaults = (
  quote: Pick<Quote, 'title' | 'details'>,
  todayIso: string,
): AcceptFrameworkValues => {
  const today = todayIso.slice(0, 10);
  return {
    contractNumber: '',
    title: quote.title || '',
    validFrom: quote.details?.projectStart || today,
    validUntil: quote.details?.projectEnd || '',
    customerRef: '',
    orderDate: today,
    note: '',
    makeDefault: true,
  };
};

/**
 * Baut den Rahmenvertrag. Die Werte aus dem Dialog gelten so, wie sie dort
 * stehen — ein geleertes „Gültig bis" heisst unbefristet und faellt NICHT
 * still auf das Projektende des Angebots zurueck.
 */
export const buildFrameworkContractFromQuote = (
  quote: Pick<Quote, 'id' | 'quoteNumber' | 'title' | 'items' | 'details' | 'discountPercent'>,
  values: AcceptFrameworkValues,
  meta: { id: string; acceptedAt: string; orderNumber?: string },
): FrameworkContract => {
  const customerRef = values.customerRef?.trim() || undefined;
  const number = values.contractNumber.trim() || customerRef || meta.orderNumber || quote.quoteNumber;
  const title = values.title.trim() || quote.title || undefined;
  const note = values.note?.trim() || undefined;
  const discount = Number(quote.discountPercent) || 0;
  return {
    id: meta.id,
    number,
    ...(title ? { title } : {}),
    ...(values.validFrom ? { validFrom: values.validFrom } : {}),
    ...(values.validUntil ? { validUntil: values.validUntil } : {}),
    ...(note ? { note } : {}),
    isDefault: !!values.makeDefault,
    items: frameworkItemsFromQuote(quote),
    ...(discount ? { discountPercent: discount } : {}),
    source: {
      quoteId: quote.id,
      quoteNumber: quote.quoteNumber,
      ...(meta.orderNumber ? { orderNumber: meta.orderNumber } : {}),
      acceptedAt: meta.acceptedAt,
      ...(values.orderDate ? { orderDate: values.orderDate } : {}),
      ...(customerRef ? { customerRef } : {}),
      ...(values.actor?.name ? { acceptedByName: values.actor.name } : {}),
    },
  };
};

/**
 * Haengt den Vertrag an die Liste des Kunden. Idempotent ueber die
 * Angebots-id: steht schon ein Vertrag aus diesem Angebot dort (Wiederholung
 * nach einem Abbruch), bleibt die Liste unveraendert und der vorhandene gilt.
 * Ein neuer Standard nimmt allen anderen das Standard-Kennzeichen — es gibt
 * genau einen je Kunde.
 */
export const addFrameworkContract = (
  existing: FrameworkContract[],
  contract: FrameworkContract,
): { list: FrameworkContract[]; contract: FrameworkContract; added: boolean } => {
  const quoteId = contract.source?.quoteId;
  const prior = quoteId ? existing.find((c) => c.source?.quoteId === quoteId) : undefined;
  if (prior) return { list: existing, contract: prior, added: false };
  const base = contract.isDefault
    ? existing.map((c) => (c.isDefault ? { ...c, isDefault: false } : c))
    : existing;
  return { list: [...base, contract], contract, added: true };
};

/** Vertragsinhalt ohne Standard-Kennzeichen — das wird getrennt entschieden. */
const contractBody = (c: FrameworkContract): string => {
  const { isDefault: _ignored, ...rest } = c;
  return JSON.stringify(rest);
};

/**
 * Rahmenvertraege beim Speichern der Kundenakte mit dem Server abgleichen
 * (Dreiweg: `base` = beim Laden, `draft` = in der Akte bearbeitet,
 * `server` = frisch gelesen).
 *
 * Die Akte schreibt `framework_contracts` als GANZE Liste. War sie mit einem
 * aelteren Stand offen, waehrend anderswo ein Angebot als Rahmenvertrag
 * angenommen oder ein Vertrag geaendert wurde, ueberschrieb ihr Speichern das
 * still. Je Vertrag gilt deshalb:
 *  - in der Akte neu angelegt → bleibt;
 *  - in der Akte entfernt → bleibt entfernt;
 *  - in der Akte bearbeitet → die Akte gewinnt (auch gegen eine Loeschung);
 *  - in der Akte unberuehrt → Serverstand (Aenderung bzw. Loeschung anderswo);
 *  - nur auf dem Server (anderswo angelegt) → kommt dazu.
 * Standard: hoechstens einer. Hat die Akte ihn neu gewaehlt, gilt ihre Wahl,
 * sonst der des Servers.
 *
 * Aendert sich nichts, kommt `draft` selbst zurueck (Referenz), damit der
 * Aufrufer „nichts zu tun" erkennt.
 */
export const mergeFrameworkContractsOnSave = (
  base: FrameworkContract[],
  draft: FrameworkContract[],
  server: FrameworkContract[],
): FrameworkContract[] => {
  const baseById = new Map(base.map((c) => [c.id, c]));
  const serverById = new Map(server.map((c) => [c.id, c]));
  const draftIds = new Set(draft.map((c) => c.id));
  const out: FrameworkContract[] = [];
  for (const d of draft) {
    const b = baseById.get(d.id);
    const s = serverById.get(d.id);
    if (!b) { out.push(d); continue; }
    const editedHere = contractBody(d) !== contractBody(b);
    if (!s) { if (editedHere) out.push(d); continue; }
    out.push(editedHere || contractBody(s) === contractBody(d) ? d : s);
  }
  for (const s of server) {
    if (!draftIds.has(s.id) && !baseById.has(s.id)) out.push(s);
  }

  const present = new Set(out.map((c) => c.id));
  const draftDefault = draft.find((c) => c.isDefault)?.id;
  const baseDefault = base.find((c) => c.isDefault)?.id;
  const serverDefault = server.find((c) => c.isDefault)?.id;
  // Neu gewaehlt (nicht nur den alten Standard geloescht) = bewusste Wahl der
  // Akte. Sonst entscheidet allein der Server — ein alter Standard der Akte
  // kaeme sonst gegen eine Aenderung anderswo zurueck.
  const choseHere = !!draftDefault && draftDefault !== baseDefault;
  const candidate = choseHere ? draftDefault : serverDefault;
  const winner = candidate && present.has(candidate) ? candidate : undefined;
  const result = out.map((c) => {
    const isDefault = !!winner && c.id === winner;
    return !!c.isDefault === isDefault ? c : { ...c, isDefault };
  });
  return result.length === draft.length && result.every((c, i) => c === draft[i]) ? draft : result;
};

/** Rahmenvertraege eines Kunden, die vereinbarte Preise tragen. */
export const frameworkContractsWithItems = (customer: Pick<Customer, 'frameworkContracts'> | null | undefined): FrameworkContract[] =>
  (customer?.frameworkContracts || []).filter((c) => (c.items?.length ?? 0) > 0);

export type FrameworkValidity = 'active' | 'expired' | 'upcoming';

/** Gueltigkeit am Stichtag (yyyy-mm-dd bzw. ISO). */
export const frameworkContractValidity = (
  c: Pick<FrameworkContract, 'validFrom' | 'validUntil'>,
  todayIso: string,
): FrameworkValidity => {
  const today = todayIso.slice(0, 10);
  if (c.validUntil && c.validUntil < today) return 'expired';
  if (c.validFrom && c.validFrom > today) return 'upcoming';
  return 'active';
};

export const FRAMEWORK_VALIDITY_LABELS: Record<FrameworkValidity, string> = {
  active: 'gültig',
  expired: 'abgelaufen',
  upcoming: 'noch nicht gültig',
};

/**
 * Positionen fuers Projekt-LV (Projekt-Assistent). Einheitspreis = vereinbarter
 * Nettopreis; die Menge setzt der Anlegende (Vorgabe 1 wie aus dem Katalog).
 * NEP traegt is_optional wie bei der Auftragsannahme.
 */
export const frameworkContractToServiceItems = (
  c: Pick<FrameworkContract, 'items' | 'discountPercent'>,
  idFactory: () => string = () => crypto.randomUUID(),
): ServiceItem[] =>
  (c.items || []).map((it) => ({
    id: idFactory(),
    lvPosition: it.lvPosition || it.posNumber || '',
    title: it.title,
    unit: it.unit,
    quantityContracted: 1,
    quantityUsed: 0,
    unitPrice: frameworkItemNetUnitPrice(it, c.discountPercent),
    status: 'open' as const,
    ...(it.category ? { category: it.category } : {}),
    assignment: it.assignment || 'none',
    ...(it.nep ? { is_optional: true } : {}),
  }));

/**
 * Positionen fuer ein neues Angebot: vereinbarter Nettopreis als Einheitspreis,
 * ohne weiteren Positionsrabatt (der steckt schon im Preis).
 */
export const frameworkContractToQuoteItems = (
  c: Pick<FrameworkContract, 'items' | 'discountPercent'>,
  sectionId?: string,
  idFactory: () => string = () => crypto.randomUUID(),
): QuoteItem[] =>
  (c.items || []).map((it) => ({
    id: idFactory(),
    ...(it.catalogItemId ? { catalogItemId: it.catalogItemId } : {}),
    lvPosition: it.lvPosition,
    title: it.title,
    unit: it.unit,
    quantity: 1,
    unitPrice: frameworkItemNetUnitPrice(it, c.discountPercent),
    discountPercent: 0,
    ...(it.category ? { category: it.category } : {}),
    ...(it.assignment ? { assignment: it.assignment } : {}),
    ...(sectionId ? { sectionId } : {}),
    ...(it.nep ? { nep: true } : {}),
    ...(it.bemerkung ? { bemerkung: it.bemerkung } : {}),
  }));
